import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchProductDetails } from '../services/apiService';
import { FaArrowLeft, FaBoxes, FaImages, FaTimes, FaChevronLeft, FaChevronRight, FaInfoCircle, FaMoneyBill, FaShieldAlt, FaBuilding, FaBookOpen, FaClipboardList } from 'react-icons/fa';
import LoadingSpinner from './LoadingSpinner';
import { useLanguage } from '../contexts/LanguageContext';
import { normalizeImageArray, getImageUrl } from '../utils/imageUtils';
import PriceDisplay from './PriceDisplay';
import SafeHtml from './SafeHtml';

const ProductDetails = () => {
  const { pharmacode } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [product, setProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImages, setModalImages] = useState([]);
  const [modalIndex, setModalIndex] = useState(0);

  useEffect(() => {
    const loadProduct = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetchProductDetails(pharmacode);
        if (response.success && response.data && response.data.product) {
          setProduct(response.data.product);
        } else {
          setError(t('productDetails.errors.productNotFound'));
        }
      } catch (err) {
        setError(t('productDetails.errors.loadFailed'));
      } finally {
        setIsLoading(false);
      }
    };
    loadProduct();
  }, [pharmacode]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pharmacode]);

  // Modal handlers
  const openImageModal = (images) => {
    setModalImages(images);
    setModalIndex(0);
    setModalOpen(true);
  };
  const closeModal = () => setModalOpen(false);
  const prevImage = () => setModalIndex((idx) => (idx === 0 ? modalImages.length - 1 : idx - 1));
  const nextImage = () => setModalIndex((idx) => (idx === modalImages.length - 1 ? 0 : idx + 1));

  const renderList = (items, label) => (
    items && items.length > 0 ? (
      <ul className="list-disc ml-6 text-gray-600 text-sm">
        {items.map((item, idx) => <li key={idx}>{item}</li>)}
      </ul>
    ) : (
      <div className="text-gray-500 italic">{t('productDetails.noInfo.forLabel', { label })}</div>
    )
  );

  const renderObject = (obj, label) => (
    obj ? (
      <div className="text-gray-600 text-sm">
        {Object.entries(obj).map(([k, v]) => (
          <div key={k}><span className="font-medium">{k}:</span> {v ?? t('productDetails.noInfo.available')}</div>
        ))}
      </div>
    ) : (
      <div className="text-gray-500 italic">{t('productDetails.noInfo.forLabel', { label })}</div>
    )
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-96">
        <LoadingSpinner size="lg" />
        <span className="ml-3 text-lg text-gray-600">{t('productDetails.loading')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto mt-10 p-6 bg-red-50 border border-red-200 rounded-lg text-red-700 flex flex-col items-center">
        <span>{error}</span>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg hover:from-indigo-600 hover:to-purple-700"
        >
          <FaArrowLeft className="inline mr-2" /> {t('productDetails.goBack')}
        </button>
      </div>
    );
  }

  const articles = product.articles || [];

  return (
    <div className="w-full mx-auto mt-4 bg-white rounded-xl border border-gray-200 shadow-sm p-8 sm:p-12 lg:p-20">
      <button
        onClick={() => navigate(-1)}
        className="mb-6 flex items-center gap-2 text-indigo-600 hover:text-indigo-800 text-sm font-medium"
      >
        <FaArrowLeft /> {t('productDetails.backToProducts')}
      </button>
      <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
        <FaBoxes className="text-indigo-600 text-2xl" /> {t('productDetails.title')}
      </h2>
      {articles.length === 0 && (
        <div className="text-gray-500 italic">{t('productDetails.noArticles')}</div>
      )}
      {articles.map((article, idx) => {
        // Helper to get image URLs
        const getImageUrls = () => {
          const images = normalizeImageArray(article, 'F');
          return images.map(img => getImageUrl(img)).filter(Boolean);
        };
        const images = getImageUrls();
        return (
          <div key={idx} className="mb-12 p-6 border rounded-2xl bg-gray-50 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-2xl font-bold text-indigo-700 flex items-center gap-2"><FaBoxes /> {t('productDetails.article', { number: idx + 1 })}</h3>
              {images.length > 0 ? (
                <button
                  onClick={() => openImageModal(images)}
                  className="flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg shadow hover:from-indigo-600 hover:to-purple-700 transition-all text-sm"
                >
                  <FaImages /> {t('productDetails.viewImages', { count: images.length })}
                </button>
              ) : null}
            </div>
            
            {/* Table-like layout for all sections */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2 text-lg font-semibold text-gray-800"><FaInfoCircle /> {t('productDetails.sections.basicInfo')}</div>
              <div className="w-full border rounded-lg overflow-hidden">
                <div className="grid grid-cols-2">
                  <div className="bg-gray-100 px-4 py-2 font-semibold">{t('productDetails.fields.pharmacode')}</div>
                  <div className="bg-gray-100 px-4 py-2">{article.pharmacode ?? t('productDetails.noInfo.available')}</div>
                  <div className="bg-gray-50 px-4 py-2 font-semibold">{t('productDetails.fields.brand')}</div>
                  <div className="bg-gray-50 px-4 py-2">{article.brand ?? product.brand ?? t('productDetails.noInfo.available')}</div>
                  <div className="bg-gray-100 px-4 py-2 font-semibold">{t('productDetails.fields.description')}</div>
                  <div className="bg-gray-100 px-4 py-2">{article.description?.description ?? t('productDetails.noInfo.available')}</div>
                  <div className="bg-gray-50 px-4 py-2 font-semibold">{t('productDetails.fields.longDescription')}</div>
                  <div className="bg-gray-50 px-4 py-2">{article.description?.longDescription ?? t('productDetails.noInfo.available')}</div>
                  <div className="bg-gray-100 px-4 py-2 font-semibold">{t('productDetails.fields.hasPhoto')}</div>
                  <div className="bg-gray-100 px-4 py-2">{article.hasPhoto ? t('productDetails.yes') : t('productDetails.no')}</div>
                </div>
              </div>
            </div>
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2 text-lg font-semibold text-green-800"><FaMoneyBill /> {t('productDetails.sections.pricing')}</div>
              <div className="w-full border rounded-lg overflow-hidden">
                <div className="grid grid-cols-2">
                  <div className="bg-gray-100 px-4 py-2 font-semibold">{t('productDetails.fields.price')}</div>
                  <div className="px-4 py-2">
                    <PriceDisplay article={article} fallbackKey="productDetails.noInfo.priceInfo" />
                  </div>
                </div>
              </div>
            </div>
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2 text-lg font-semibold text-blue-800"><FaShieldAlt /> {t('productDetails.sections.regulatory')}</div>
              <div className="w-full border rounded-lg overflow-hidden">
                <div className="grid grid-cols-2">
                  <div className="bg-gray-100 px-4 py-2 font-semibold">{t('productDetails.fields.regulations')}</div>
                  <div className="px-4 py-2">
                    {article.regulations && article.regulations.length > 0 ? (
                      <ul className="list-disc ml-6 text-gray-600 text-sm">
                        {article.regulations.map((reg, i) => (
                          <li key={i}>
                            <span className="font-medium">{reg.type}:</span> {reg.code?.description || reg.code?.id || t('productDetails.noInfo.available')}
                            {reg.isCurrent && <span className="ml-2 text-green-600 font-semibold">{t('productDetails.current')}</span>}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-gray-500 italic">{t('productDetails.noInfo.regulationsInfo')}</div>
                    )}
                  </div>
                  <div className="bg-gray-50 px-4 py-2 font-semibold">{t('productDetails.fields.insuranceCode')}</div>
                  <div className="px-4 py-2">
                    {article.insuranceCode ? (
                      <div>
                        {Object.entries(article.insuranceCode).map(([k, v]) => (
                          <div key={k}><span className="font-medium">{k}:</span> {v ?? t('productDetails.noInfo.available')}</div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-gray-500 italic">{t('productDetails.noInfo.insuranceInfo')}</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2 text-lg font-semibold text-purple-800"><FaBuilding /> {t('productDetails.sections.companyCodes')}</div>
              <div className="w-full border rounded-lg overflow-hidden">
                <div className="grid grid-cols-2">
                  <div className="bg-gray-100 px-4 py-2 font-semibold">{t('productDetails.fields.companies')}</div>
                  <div className="px-4 py-2">{renderList(article.companies, t('productDetails.fields.companies').toLowerCase())}</div>
                  <div className="bg-gray-50 px-4 py-2 font-semibold">{t('productDetails.fields.substances')}</div>
                  <div className="px-4 py-2">{renderList(article.substances, t('productDetails.fields.substances').toLowerCase())}</div>
                  <div className="bg-gray-100 px-4 py-2 font-semibold">{t('productDetails.fields.genericGroupCodes')}</div>
                  <div className="px-4 py-2">{renderList(article.genericGroupCodes, t('productDetails.fields.genericGroupCodes').toLowerCase())}</div>
                </div>
              </div>
            </div>
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2 text-lg font-semibold text-indigo-800"><FaClipboardList /> {t('productDetails.sections.consumerInfo')}</div>
              <div className="w-full border rounded-lg overflow-hidden">
                <div className="grid grid-cols-2">
                  <div className="bg-gray-100 px-4 py-2 font-semibold">{t('productDetails.fields.consumerFolder')}</div>
                  <div className="px-4 py-2">
                    {article.consumerFolder && article.consumerFolder.length > 0 ? (
                      article.consumerFolder.map((folder, i) => (
                        <div key={i} className="mb-2">
                          <div className="font-medium text-indigo-800">{folder.title ?? t('productDetails.noInfo.title')}</div>
                          {folder.summary ? (
                            <SafeHtml className="text-xs text-gray-700 mt-1" html={folder.summary} />
                          ) : (
                            <div className="text-gray-500 italic">{t('productDetails.noInfo.summary')}</div>
                          )}
                          {folder.content ? (
                            <SafeHtml className="text-xs text-gray-700 mt-1" html={folder.content} />
                          ) : null}
                        </div>
                      ))
                    ) : (
                      <div className="text-gray-500 italic">{t('productDetails.noInfo.consumerFolderInfo')}</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2 text-lg font-semibold text-pink-800"><FaBookOpen /> {t('productDetails.sections.monographies')}</div>
              <div className="w-full border rounded-lg overflow-hidden">
                <div className="grid grid-cols-2">
                  <div className="bg-gray-100 px-4 py-2 font-semibold">{t('productDetails.fields.monographies')}</div>
                  <div className="px-4 py-2">
                    {article.monographies && article.monographies.length > 0 ? (
                      article.monographies.map((mono, i) => (
                        <div key={i} className="mb-2">
                          <div className="font-medium text-purple-800">{mono.title ?? t('productDetails.noInfo.title')}</div>
                          {mono.data?.bodySections && mono.data.bodySections.length > 0 ? (
                            mono.data.bodySections.map((section, sidx) => (
                              <div key={sidx} className="mt-2">
                                <div className="font-semibold text-gray-700 text-sm mb-1">{section.title ?? t('productDetails.noInfo.sectionTitle')}</div>
                                <SafeHtml className="text-xs text-gray-700" html={section.description ?? t('productDetails.noInfo.description')} />
                              </div>
                            ))
                          ) : (
                            <div className="text-gray-500 italic">{t('productDetails.noInfo.bodySections')}</div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="text-gray-500 italic">{t('productDetails.noInfo.monographiesInfo')}</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="mb-2">
              <div className="flex items-center gap-2 mb-2 text-lg font-semibold text-yellow-800"><FaClipboardList /> {t('productDetails.sections.nonPharmaInfo')}</div>
              <div className="w-full border rounded-lg overflow-hidden">
                <div className="grid grid-cols-2">
                  <div className="bg-gray-100 px-4 py-2 font-semibold">{t('productDetails.fields.nonPharmaInformation')}</div>
                  <div className="px-4 py-2">
                    {article.nonPharmaInformation && article.nonPharmaInformation.length > 0 ? (
                      article.nonPharmaInformation.map((info, i) => (
                        <div key={i} className="mb-2">
                          <div className="font-medium text-yellow-800">{info.title ?? t('productDetails.noInfo.title')}</div>
                          {info.summaryHtml && (
                            <SafeHtml className="text-xs text-gray-700 mt-1" html={info.summaryHtml} />
                          )}
                          {info.usageHtml && (
                            <SafeHtml className="text-xs text-gray-700 mt-1" html={info.usageHtml} />
                          )}
                          {info.characteristicsHtml && (
                            <SafeHtml className="text-xs text-gray-700 mt-1" html={info.characteristicsHtml} />
                          )}
                          {info.compositions && info.compositions.length > 0 && (
                            <div className="mt-2">
                              <div className="font-semibold text-gray-700 text-sm mb-1">{t('productDetails.fields.compositions')}</div>
                              <ul className="list-disc ml-6 text-gray-600 text-xs">
                                {info.compositions.map((comp, cidx) => (
                                  <li key={cidx}>
                                    {comp.description ?? t('productDetails.noInfo.description')}
                                    {comp.quantity && (
                                      <> - {comp.quantity}{comp.unitCode?.id ? ` ${comp.unitCode.id}` : ''}</>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {/* Show other fields if needed */}
                          <div className="text-xs text-gray-500 mt-1">
                            {info.createdDate && <div>Created: {info.createdDate}</div>}
                            {info.changedDate && <div>Changed: {info.changedDate}</div>}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-gray-500 italic">{t('productDetails.noInfo.nonPharmaInfo')}</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
      {/* Modal overlay (global, not per-article) */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
          <div className="relative bg-white rounded-lg shadow-lg p-4 max-w-lg w-full flex flex-col items-center">
            <button
              onClick={closeModal}
              className="absolute top-2 right-2 text-gray-600 hover:text-red-600 text-xl"
              aria-label={t('productDetails.modal.close')}
            >
              <FaTimes />
            </button>
            <div className="flex items-center justify-center w-full h-80">
              <button
                onClick={prevImage}
                className="text-gray-500 hover:text-indigo-600 text-2xl px-2"
                aria-label={t('productDetails.modal.previousImage')}
                disabled={modalImages.length <= 1}
              >
                <FaChevronLeft />
              </button>
              <img
                src={modalImages[modalIndex]}
                alt={`Product image ${modalIndex + 1}`}
                className="max-h-72 max-w-full rounded-lg border shadow object-contain bg-white mx-4"
              />
              <button
                onClick={nextImage}
                className="text-gray-500 hover:text-indigo-600 text-2xl px-2"
                aria-label={t('productDetails.modal.nextImage')}
                disabled={modalImages.length <= 1}
              >
                <FaChevronRight />
              </button>
            </div>
            <div className="mt-2 text-gray-700 text-sm">
              {t('productDetails.modal.imageCounter', { current: modalIndex + 1, total: modalImages.length })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetails; 